import React, { useEffect, useState, useRef } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
import { FilterPanel } from './FilterPanel';
interface BudgetPopoverProps {
  minBudget: number | null;
  maxBudget: number | null;
  onChange: (min: number | null, max: number | null) => void;
}
export function BudgetPopover({
  minBudget,
  maxBudget,
  onChange
}: BudgetPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Local input state — strings while typing.
  const [localMin, setLocalMin] = useState(minBudget?.toString() ?? '');
  const [localMax, setLocalMax] = useState(maxBudget?.toString() ?? '');
  // Re-sync local inputs whenever the popover opens or the controlled values change.
  useEffect(() => {
    setLocalMin(minBudget?.toString() ?? '');
    setLocalMax(maxBudget?.toString() ?? '');
  }, [minBudget, maxBudget, isOpen]);
  const handleApply = () => {
    const min = localMin.trim() ? parseInt(localMin, 10) : null;
    const max = localMax.trim() ? parseInt(localMax, 10) : null;
    onChange(Number.isFinite(min as number) ? min as number | null : null, Number.isFinite(max as number) ? max as number | null : null);
    setIsOpen(false);
  };
  const handleClear = () => {
    setLocalMin('');
    setLocalMax('');
    onChange(null, null);
    setIsOpen(false);
  };
  const getLabel = () => {
    if (minBudget != null && maxBudget != null) return `${minBudget}-${maxBudget} €`;
    if (minBudget != null) return `Min ${minBudget} €`;
    if (maxBudget != null) return `Max ${maxBudget} €`;
    return 'Orice buget';
  };
  const isActive = minBudget !== null || maxBudget !== null;
  return <div className="relative min-w-0 sm:flex-1" ref={ref} data-id="element-2326">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={cn('w-full h-10 px-4 rounded-xl border text-sm flex items-center justify-between gap-2 transition-colors cursor-pointer', isActive ? 'bg-dark-2 border-lime-1 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-light-4')} data-id="element-2327">
        <span className="truncate min-w-0" data-id="element-2328">{getLabel()}</span>
        <CaretDown size={14} className={cn('shrink-0 transition-transform', isOpen && 'rotate-180')} data-id="element-2329" />
      </button>

      <FilterPanel isOpen={isOpen} onClose={() => setIsOpen(false)} title="Doar bugete între" anchorRef={ref} desktopWidthClass="sm:w-80" footer={<div className="flex items-center justify-between gap-3" data-id="element-2331">
            <button type="button" onClick={handleClear} className="text-sm text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2332">
              Resetează
            </button>
            <button type="button" onClick={handleApply} className="h-11 px-5 rounded-xl bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-2333">
              Aplică
            </button>
          </div>} data-id="element-2330">
        <div className="p-4" data-id="element-2334">
          {/* Desktop-only inline title (mobile shows it in the sheet header) */}
          <h4 className="hidden sm:block text-sm font-medium text-light-1 mb-4" data-id="element-2335">
            Doar bugete între
          </h4>

          <div className="flex items-end gap-3" data-id="element-2336">
            <div className="flex-1 min-w-0" data-id="element-2337">
              <label htmlFor="budget-min" className="block text-[10px] font-semibold uppercase tracking-wider text-light-4 mb-1.5" data-id="element-2338">
                Min.
              </label>
              <div className="relative" data-id="element-2339">
                <input id="budget-min" type="number" inputMode="numeric" min={0} value={localMin} onChange={e => setLocalMin(e.target.value)} className="w-full h-10 bg-dark-3 border border-dark-4 rounded-lg pl-3 pr-8 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 focus:ring-1 focus:ring-lime-1" placeholder="0" data-id="element-2340" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-light-3 pointer-events-none" data-id="element-2341">
                  €
                </span>
              </div>
            </div>

            <span className="pb-3 text-light-4 text-sm leading-none" data-id="element-2342">—</span>

            <div className="flex-1 min-w-0" data-id="element-2343">
              <label htmlFor="budget-max" className="block text-[10px] font-semibold uppercase tracking-wider text-light-4 mb-1.5" data-id="element-2344">
                Max.
              </label>
              <div className="relative" data-id="element-2345">
                <input id="budget-max" type="number" inputMode="numeric" min={0} value={localMax} onChange={e => setLocalMax(e.target.value)} className="w-full h-10 bg-dark-3 border border-dark-4 rounded-lg pl-3 pr-8 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 focus:ring-1 focus:ring-lime-1" placeholder="Orice" data-id="element-2346" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-light-3 pointer-events-none" data-id="element-2347">
                  €
                </span>
              </div>
            </div>
          </div>
        </div>
      </FilterPanel>
    </div>;
}